/**
 * Reading the co-pilot's latency counters back out - pure, no React.
 *
 * The server stores a sum and a count rather than an average, because no
 * database can maintain a running mean atomically. The division therefore
 * happens here, and this file is the reader's half of the rule written in
 * `crm-calls/lib/pilot/latency.ts` - kept as strict as the server's, the same
 * way `transcript.ts` mirrors the server's timed-turn reader.
 */

/** The columns the pane needs. */
export interface PilotLatencyCounters {
  pilotLatencyMsSum: number;
  pilotLatencySamples: number;
}

/**
 * The average window round trip, or `null` when nothing was ever timed.
 *
 * Null rather than zero on an untimed call, for the reason every other absent
 * number in this pane is null: a zero here would claim the co-pilot answered
 * instantly, which is a much stronger statement than "it never ran".
 */
export function pilotLatencyAvgMs(row: PilotLatencyCounters): number | null {
  if (row.pilotLatencySamples <= 0) return null;
  return Math.round(row.pilotLatencyMsSum / row.pilotLatencySamples);
}

/**
 * A duration in the unit that reads best at its own size.
 *
 * Milliseconds up to ten seconds, whole seconds above. No decimal point at any
 * size, deliberately: the separator differs between the two catalogues this
 * interface ships in, and a latency is not a number anybody needs to a tenth.
 */
export function fmtLatency(ms: number): string {
  const safe = Math.max(0, Math.round(ms));
  return safe < 10_000 ? `${safe} ms` : `${Math.round(safe / 1000)} s`;
}
